import isPlainObject from './isPlainObject';
import { withRecords } from './transitImmutable';
import rsvp from 'rsvp';

let raf, rafContext;

function setRequestAnimationFrame(newRequestAnimationFrame, context) {
  if (newRequestAnimationFrame) {
    raf = newRequestAnimationFrame;
    rafContext = context;
  } else {
    raf = __BROWSER__ ? requestAnimationFrame : setImmediate;
    rafContext = __BROWSER__ ? window : this;
  }
}

setRequestAnimationFrame();

/**
 * These are private action types reserved by Redux.
 * For any unknown actions, you must return the current state.
 * If the current state is undefined, you must return the initial state.
 * Do not reference these action types directly in your code.
 */
export var ActionTypes = {
  INIT: '@@redux/INIT',
};

/**
 * Creates a Redux store that holds the state tree.
 * The only way to change the data in the store is to call `dispatch()` on it.
 *
 * There should only be a single store in your app. To specify how different
 * parts of the state tree respond to actions, you may combine several reducers
 * into a single reducer function by using `combineReducers`.
 *
 * @param {Function} reducer A function that returns the next state tree, given
 * the current state tree and the action to handle.
 *
 * @param {any} [initialState] The initial state. You may optionally specify it
 * to hydrate the state from the server in universal apps, or to restore a
 * previously serialized user session.
 * If you use `combineReducers` to produce the root reducer function, this must be
 * an object with the same shape as `combineReducers` keys.
 *
 * @returns {Store} A Redux store that lets you read the state, dispatch actions
 * and subscribe to changes.
 */
export default function createStore(reducer, initialState, enhancer) {
  if (typeof initialState === 'function' && typeof enhancer === 'undefined') {
    enhancer = initialState;
    initialState = undefined;
  }

  if (typeof enhancer === 'function') {
    return enhancer(createStore)(reducer, initialState);
  }

  let transitInstance;
  let currentReducer = reducer;
  let currentState = initialState;
  const listeners = [];
  const events = [];
  let isDispatching = false;
  let records = [];
  let rollbar = false;
  let currentTranslator = null;
  let currentNavigator = null;
  let apiFunction = null;

  function createEvent(type, ...args) {
    events.push({ type, args });
  }

  /**
   * Reads the state tree managed by the store.
   *
   * @returns {any} The current state tree of your application.
   */
  function getState() {
    return currentState;
  }

  function getIn(path) {
    return currentState.getIn(path);
  }

  function get(key) {
    return currentState.get(key);
  }

  function getTranslator() {
    return currentTranslator;
  };

  function setTranslator(translator) {
    currentTranslator = translator;
  };

  function setApi(api) {
    apiFunction = api;
  };

  function hasApi() {
    return !!apiFunction;
  };

  function callApi(...args) {
    return apiFunction(this, ...args);
  };

  function logPageView(path, loadTime) {
    if (!__BROWSER__ || !__GOOGLE_ANALYTICS_TOKEN__) return;

    const url = `https://www.google-analytics.com/collect?v=1&tid=${__GOOGLE_ANALYTICS_TOKEN__}` +
     `&cid=555&t=pageview&dh=${encodeURI(window.location.hostname)}` +
     `&dt=${encodeURI(document.title)}&dp=${encodeURI(path)}` + (loadTime ? `&plt=${loadTime}` : '');

    store.callApi({ contentType: 'text', method: 'POST', url }).catch((e) => {
      // console.log('Ignore api errors from ga');
    });
  }

  /**
   * Adds 'Record' types required for reading/writing transit format.
   */
  function addRecords(newRecords) {
    records = records.concat(newRecords);
  };

  function initTransit() {
    transitInstance = withRecords(records, 'NoneNoOp');
  };

  function read(transitString) {
    if (!transitInstance) initTransit();
    return transitInstance.fromJSON(transitString);
  };

  function write(data) {
    if (!transitInstance) initTransit();
    return transitInstance.toJSON(data); //sceneGraph: Map(), files: Map()});
  };

  function useRollbar(rb) {
    rollbar = rb;
  };

  function error(err, label, data={}) {
    if (label) console.error(label);
    if (rollbar) {
      rollbar.error(err, data);
      if (err.message) {
        console.error(err.message);
        if (err.stack) console.log(err.stack);
      } else {
        console.error(err);
      }
    } else {
      throw err;
    }
  };

  rsvp.on('error', error);

  /**
   * Adds a change listener. It will be called any time an action is dispatched,
   * and some part of the state tree may potentially have changed. You may then
   * call `getState()` to read the current state tree inside the callback.
   *
   * @param {Function} listener A callback to be invoked on every dispatch.
   * @returns {Function} A function to remove this change listener.
   */
  function subscribe(listener) {
    listeners.push(listener);
    var isSubscribed = true;

    return function unsubscribe() {
      if (!isSubscribed) {
        return;
      }

      isSubscribed = false;
      var index = listeners.indexOf(listener);
      listeners.splice(index, 1);
    };
  }

  // Custom debounce function, lodash.debounce is bigger and slower.
  function batchNotify() {
    var notifying = false;
    var requestNotify = false;

    function notify() {
      if (notifying) {
        requestNotify = true;
      } else {
        notifying = true;
        requestNotify = false;

        raf.call(rafContext, () => {
          // Actual notify code
          try {
            var evs = events.splice(0, events.length);
            listeners.slice().forEach(listener => listener(evs));
          } catch (e) {
            error(e);
          }

          notifying = false;
          if (requestNotify) {
            notify();
          }
        });
      }
    }

    return notify;
  }

  const notifyListenersBatched = batchNotify();

  /**
   * Dispatches an action. It is the only way to trigger a state change.
   *
   * The `reducer` function, used to create the store, will be called with the
   * current state tree and the given `action`. Its return value will
   * be considered the **next** state of the tree, and the change listeners
   * will be notified.
   *
   * The base implementation only supports plain object actions. If you want to
   * dispatch a Promise, an Observable, a thunk, or something else, you need to
   * wrap your store creating function into the corresponding middleware. For
   * example, see the documentation for the `redux-thunk` package. Even the
   * middleware will eventually dispatch plain object actions using this method.
   *
   * @param {Object} action A plain object representing “what changed”. It is
   * a good idea to keep actions serializable so you can record and replay user
   * sessions, or use the time travelling `redux-devtools`. An action must have
   * a `type` property which may not be `undefined`. It is a good idea to use
   * string constants for action types.
   *
   * @returns {Object} For convenience, the same action object you dispatched.
   *
   * Note that, if you use a custom middleware, it may wrap `dispatch()` to
   * return something else (for example, a Promise you can await).
   */
  function dispatch(action) {
    if (!isPlainObject(action)) {
      throw new Error(
        'Actions must be plain objects. ' +
        'Use custom middleware for async actions.'
      );
    }

    if (typeof action.type === 'undefined') {
      console.error('Invalid action: ', action);
      throw new Error(
        'Actions may not have an undefined "type" property. ' +
        'Have you misspelled a constant?'
      );
    }

    if (isDispatching) {
      throw new Error('Reducers may not dispatch actions.');
    }

    try {
      isDispatching = true;
      currentState = currentReducer(currentState, action, createEvent);
    } catch (e) {
      error(e);
    } finally {
      isDispatching = false;
    }

    notifyListenersBatched();
    // listeners.slice().forEach(listener => listener())

    return rsvp.Promise.resolve(action.resolve ? action.resolve : action.payload);
  }


  /**
   * Replaces the reducer currently used by the store to calculate the state.
   *
   * You might need this if your app implements code splitting and you want to
   * load some of the reducers dynamically. You might also need this if you
   * implement a hot reloading mechanism for Redux.
   *
   * @param {Function} nextReducer The reducer for the store to use instead.
   * @returns {void}
   */
  function replaceReducer(nextReducer) {
    currentReducer = nextReducer;
    dispatch({ type: ActionTypes.INIT });
  }

  // When a store is created, an "INIT" action is dispatched so that every
  // reducer returns their initial state. This effectively populates
  // the initial state tree.
  dispatch({ type: ActionTypes.INIT });

  const store = {
    dispatch,
    subscribe,
    getState,
    getIn,
    get,
    replaceReducer,
    setTranslator,
    getTranslator,
    addRecords,
    read,
    write,
    useRollbar,
    error,
    setApi,
    hasApi,
    callApi,
    createEvent,
    setRequestAnimationFrame,
    logPageView,
  };

  return store;
}



// WEBPACK FOOTER //
// ./src/store/createStore.js